import React, { JSX, useState } from "react";
import { Collapse } from "react-bootstrap";
import { LoadingContainer } from "go-core/components/Loading";
import { ReactComponent as CollapseSVG } from "go-core/images/svg/actions/collapse.svg";
import { ReactComponent as SortDownSVG } from "go-list/core/images/sort-down.svg";
import { ReactComponent as SortUpDownSVG } from "go-list/core/images/sort-up-down.svg";
import { ReactComponent as SortUpSVG } from "go-list/core/images/sort-up.svg";
import { UserApi } from "../../../../../../../services/Api/types";
import { SupportDistrictApi, SupportOrganizationApi } from "../../../../../../../services/support/types";

interface HeaderColumn {
	title: string;
	sort?: boolean;
	id: string;
}

interface Props {
	title: string;
	headerColumns: HeaderColumn[];
	dataColumns: (item: any) => JSX.Element[];
	items: (SupportOrganizationApi | UserApi | SupportDistrictApi)[];
	selectedSorts?: string[];
	setSelectedSorts?: (sorts: string[]) => void;
	loading?: boolean;
}

const DropdownList = ({
	title,
	dataColumns,
	headerColumns,
	items,
	selectedSorts,
	setSelectedSorts,
	loading,
}: Props) => {
	const [collapseIn, setCollapseIn] = useState(true);

	const onCollapse = () => {
		const collapse = collapseIn;
		setCollapseIn(!collapse);
		const icon = document.getElementById(`table-${title}-collapse-icon`);
		if (icon) {
			icon.style.transform = !collapse ? "" : "rotate(360deg)";
		}
	};

	const drawSortIcon = (column: HeaderColumn) => {
		if (column.sort) {
			if (selectedSorts?.includes(column.id)) return <SortUpSVG />;
			if (selectedSorts?.includes(`-${column.id}`)) return <SortDownSVG />;
			return <SortUpDownSVG className="ms-1" />;
		}
	};

	const drawTableHeaderColumns = () => {
		return headerColumns.map((col, index) => (
			<th
				key={`table-${title}-header-col-${index}`}
				className={`${col.sort ? "sortable-column" : ""}`}
				onClick={col.sort ? () => sort(col.id) : undefined}
			>
				{col.title}
				{setSelectedSorts && drawSortIcon(col)}
			</th>
		));
	};

	const drawTableDataColumns = (item: SupportOrganizationApi | UserApi | SupportDistrictApi) => {
		return dataColumns(item).map((col, index) => <td key={`table-${title}-data-col-${index}`}>{col}</td>);
	};

	const drawItems = () => {
		return items.map((item, index) => <tr key={`table-${title}-item-${index}`}>{drawTableDataColumns(item)}</tr>);
	};

	const sort = (id: string) => {
		if (!setSelectedSorts) return;

		let sorts = [...(selectedSorts || [])];
		if (sorts) {
			if (sorts.includes(id)) {
				sorts.splice(sorts.indexOf(id), 1, `-${id}`);
			} else if (sorts.includes(`-${id}`)) {
				sorts = sorts.filter((f) => f !== `-${id}`);
			} else {
				sorts.unshift(id);
			}
			setSelectedSorts(sorts);
		}
	};

	return (
		<div className="dropdown-list">
			<div className="dropdown-list-header" onClick={onCollapse}>
				<CollapseSVG id={`table-${title}-collapse-icon`} className="collapse-icon" />
				<h5>{title}</h5>
			</div>
			<Collapse in={collapseIn}>
				<div className="table-responsive">
					{loading ? (
						<LoadingContainer />
					) : (
						<table className="table table-preview ">
							<thead>
								<tr>{drawTableHeaderColumns()}</tr>
							</thead>
							<tbody>{drawItems()}</tbody>
						</table>
					)}
				</div>
			</Collapse>
		</div>
	);
};

export default DropdownList;
